import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faPlus, faTrash, faLink, faImage } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import api from '../utils/api';
import PropTypes from 'prop-types';

const CreateProject = ({ darkMode, onProjectCreated, editProject = null }) => {
    const [formData, setFormData] = useState({
        title: editProject?.title || '',
        description: editProject?.description || '',
        imageUrl: editProject?.imageUrl || '',
        websiteLink: editProject?.websiteLink || '',
        technologies: editProject?.technologies || [''],
        type: editProject?.type || 'Web Application',
        status: editProject?.status || 'Active',
        featured: editProject?.featured || false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [imagePreview, setImagePreview] = useState(editProject?.imageUrl || '');

    const projectTypes = [
        'Web Application',
        'Mobile App',
        'Desktop App',
        'API',
        'Library',
        'Other'
    ];

    const statusOptions = [
        'Active',
        'Completed',
        'In Progress',
        'Archived'
    ];

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleImageUrlChange = (e) => {
        const url = e.target.value;
        setFormData(prev => ({ ...prev, imageUrl: url }));
        setImagePreview(url);
    };

    const addTechnology = () => {
        setFormData(prev => ({
            ...prev,
            technologies: [...prev.technologies, '']
        }));
    };

    const removeTechnology = (index) => {
        if (formData.technologies.length > 1) {
            setFormData(prev => ({
                ...prev,
                technologies: prev.technologies.filter((_, i) => i !== index)
            }));
        }
    };

    const updateTechnology = (index, value) => {
        setFormData(prev => ({
            ...prev,
            technologies: prev.technologies.map((tech, i) => 
                i === index ? value : tech
            )
        }));
    };

    const validateUrl = (url) => {
        const urlPattern =/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
        return urlPattern.test(url);
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            imageUrl: '',
            websiteLink: '',
            technologies: [''],
            type: 'Web Application',
            status: 'Active',
            featured: false
        });
        setImagePreview('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Client-side validation
        if (!formData.title.trim()) {
            toast.error('Title is required');
            setIsSubmitting(false);
            return;
        }

        if (!formData.description.trim()) {
            toast.error('Description is required');
            setIsSubmitting(false);
            return;
        }

        if (!formData.imageUrl.trim()) {
            toast.error('Image URL is required');
            setIsSubmitting(false);
            return;
        }

        if (!validateUrl(formData.imageUrl)) {
            toast.error('Please provide a valid image URL');
            setIsSubmitting(false);
            return;
        }

        if (!formData.websiteLink.trim()) {
            toast.error('Website link is required');
            setIsSubmitting(false);
            return;
        }

        if (!validateUrl(formData.websiteLink)) {
            toast.error('Please provide a valid website URL');
            setIsSubmitting(false);
            return;
        }

        // Validate technologies
        const validTechnologies = formData.technologies.filter(tech => tech.trim());
        if (validTechnologies.length === 0) {
            toast.error('At least one technology is required');
            setIsSubmitting(false);
            return;
        }

        try {
            const submitData = {
                title: formData.title.trim(),
                description: formData.description.trim(),
                imageUrl: formData.imageUrl.trim(),
                websiteLink: formData.websiteLink.trim(),
                technologies: JSON.stringify(validTechnologies),
                type: formData.type,
                status: formData.status,
                featured: formData.featured
            };

            const url = editProject ? `/projects/${editProject._id}` : '/projects/create';
            const method = editProject ? 'put' : 'post';
            
            const response = await api[method](url, submitData);

            if (response.data.success) {
                const successMessage = `Project ${editProject ? 'updated' : 'created'} successfully!`;
                toast.success(successMessage);
                
                // Only reset form if not in edit mode
                if (!editProject) {
                    resetForm();
                }
                
                // Notify parent component about the successful operation
                onProjectCreated && onProjectCreated(response.data.project);
            }
        } catch (error) {
            console.error(`Error ${editProject ? 'updating' : 'creating'} project:`, error);
            toast.error(error.response?.data?.message || `Failed to ${editProject ? 'update' : 'create'} project`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {editProject ? 'Edit Project' : 'Create New Project'}
                </h2>
            </div>

            {/* Form Container */}
            <div className={`rounded-2xl border p-6 ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } shadow-lg`}>
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                Project Title *
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                className={`w-full p-3 rounded-lg border ${
                                    darkMode 
                                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                        : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                                } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                placeholder="Enter project title"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2">
                                Project Type *
                            </label>
                            <select
                                name="type"
                                value={formData.type}
                                onChange={handleInputChange}
                                className={`w-full p-3 rounded-lg border ${
                                    darkMode 
                                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                        : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                                } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                required
                            >
                                {projectTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Description *
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows="4"
                            className={`w-full p-3 rounded-lg border ${
                                darkMode 
                                    ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                    : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none`}
                            placeholder="Describe your project..."
                            required
                        />
                    </div>

                    {/* URLs */}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                <FontAwesomeIcon icon={faImage} className="mr-2" />
                                Image URL *
                            </label>
                            <input
                                type="url"
                                name="imageUrl"
                                value={formData.imageUrl}
                                onChange={handleImageUrlChange}
                                className={`w-full p-3 rounded-lg border ${
                                    darkMode 
                                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                        : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                                } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                placeholder="https://example.com/image.jpg"
                                required
                            />
                            {imagePreview && (
                                <div className="mt-3">
                                    <img 
                                        src={imagePreview} 
                                        alt="Preview" 
                                        className="w-32 h-20 object-cover rounded-lg border"
                                        onError={() => setImagePreview('')}
                                    />
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2">
                                <FontAwesomeIcon icon={faLink} className="mr-2" />
                                Website/Demo URL *
                            </label>
                            <input
                                type="url"
                                name="websiteLink"
                                value={formData.websiteLink}
                                onChange={handleInputChange}
                                className={`w-full p-3 rounded-lg border ${
                                    darkMode 
                                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                        : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                                } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                placeholder="https://your-project.com"
                                required
                            />
                        </div>
                    </div>

                    {/* Technologies */}
                    <div>
                        <div className="flex justify-between items-center mb-3">
                            <label className="block text-sm font-medium">
                                Technologies Used *
                            </label>
                            <button
                                type="button"
                                onClick={addTechnology}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                                    darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                                } text-white transition-colors flex items-center gap-2`}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                Add Technology
                            </button>
                        </div>
                        <div className="space-y-2">
                            {formData.technologies.map((tech, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={tech}
                                        onChange={(e) => updateTechnology(index, e.target.value)}
                                        className={`flex-1 p-2 rounded-lg border ${
                                            darkMode 
                                                ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                                : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                                        } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                        placeholder="e.g., React, Node.js, MongoDB"
                                        required
                                    />
                                    {formData.technologies.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeTechnology(index)}
                                            className="p-2 text-red-500 hover:text-red-700 transition-colors"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Status and Featured */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                Status
                            </label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                className={`w-full p-3 rounded-lg border ${
                                    darkMode 
                                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                                        : 'bg-white border-gray-300 text-gray-800 focus:border-blue-500'
                                } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                            >
                                {statusOptions.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center">
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="featured"
                                    checked={formData.featured}
                                    onChange={handleInputChange}
                                    className="sr-only"
                                />
                                <div className={`relative w-11 h-6 rounded-full transition-colors ${
                                    formData.featured 
                                        ? 'bg-blue-500' 
                                        : darkMode ? 'bg-gray-600' : 'bg-gray-300'
                                }`}>
                                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                                        formData.featured ? 'translate-x-5' : 'translate-x-0'
                                    }`} />
                                </div>
                                <span className="ml-3 text-sm font-medium">Featured Project</span>
                            </label>
                        </div>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex justify-end gap-4 pt-4 border-t border-gray-600/30">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`px-6 py-3 rounded-lg font-medium text-white ${
                                darkMode 
                                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500' 
                                    : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600'
                            } disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2`}
                        >
                            {isSubmitting && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                            {isSubmitting ? (editProject ? 'Updating...' : 'Creating...') : (editProject ? 'Update Project' : 'Create Project')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

CreateProject.propTypes = {
    darkMode: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onProjectCreated: PropTypes.func,
    editProject: PropTypes.object
};

export default CreateProject;
